import React, { useContext, useState, useEffect, ReactNode } from 'react'

//Node Modules
import { useTranslation } from 'react-i18next'
import Nav from 'react-bootstrap/Nav'

//Components
import MainContentArea from 'components/MainContentArea'
import DocumentationLayout from 'components/DocumentationLayout'
import SearchModal from 'components/SearchModal'
import TOC from 'components/TOC'
import VersionPicker from 'components/VersionPicker'

//Styles
import styles from './Docset.module.scss'

//Context
import { LanguageContext } from 'context/language-context'
import { DocsetLatestContext } from 'context/docset-latest-context'
import { RequestorContext } from 'context/content-context'

//Types
import IDocset from 'types/docset'

//Util
import { VERSION_GROUP_VIYA4, DOCSET_LATEST_CFR  } from 'util/constants'
import { docset as docsetLink } from 'util/links'
import { flattenEntries, siblings } from 'util/toc'
import {setMetaDescription} from 'util/set-meta'

//Hooks
import { useIsSmallDevice } from 'hooks'

interface Props {
  docset: IDocset
  page: string
  content: React.ReactNode,
  pdfUrl: string | undefined
  onPDFDownloadClick?: () => void
}

const Docset = ({ docset, page, content, pdfUrl, onPDFDownloadClick }: Props) => {
  const isSmallDevice = useIsSmallDevice()
  const { t } = useTranslation()
  const lang = useContext(LanguageContext)
  const latestCFR = useContext(DocsetLatestContext)
  const requestorId = useContext(RequestorContext)

  const [showSearch, setShowSearch] = useState<boolean>(false)
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(!isSmallDevice)

  const title = docset.info.title.long
  const alias = docset.info.id
  const cfr = docset.info.version
  const toc = docset.toc
  const versions = docset.versions

  const { prev, next } = siblings(flattenEntries(toc), alias, page)
  const prevLink = prev && docsetLink(lang, alias, !latestCFR || latestCFR !== cfr ? cfr : DOCSET_LATEST_CFR , prev.file)
  const nextLink = next && docsetLink(lang, alias, !latestCFR || latestCFR !== cfr ? cfr : DOCSET_LATEST_CFR , next.file)

  const currentDocsetVersion = docset.versions.find(version => version.docsetName === alias && version.docsetVersion === cfr)
  const currentDocsetVersionHidden = currentDocsetVersion ? currentDocsetVersion.hide : false
  const versionPickerItems = docset.versions.filter(version => !version.omit)

  const showVersionPicker = !currentDocsetVersionHidden && versionPickerItems.length

  const versionPickerMenu = ():ReactNode => {
    let versionItems = [] as ReactNode[];
    versions.forEach(v => {
      if (!(v.omit || v.earlyAdopter) ) {
        versionItems.push(<VersionPicker.Item
          key={v.docsetVersion}
          to={`./doc/${lang}/${alias}/${v.docsetVersion === latestCFR ? DOCSET_LATEST_CFR  : v.docsetVersion}/${page}?homeOnFail${requestorId ? '&requestorId='+requestorId : ''}`}
          label={v.versionGroup === VERSION_GROUP_VIYA4 ? v.softwareVersion ? v.softwareVersion : v.docsetVersion : v.title.long}
          active={v.docsetVersion === cfr}
        />)
      }
    })
    return versionItems
  }

  const versionPicker = showVersionPicker && (
    <VersionPicker
      label={t('helpcenter-gui-icu.content.version.label')}
      variant={'light'}
      menu={versionPickerMenu()}
    />
  )

  const currentVersion = docset.info.versionGroup === VERSION_GROUP_VIYA4 && docset.info.softwareVersion ?
    <Nav.Item className={styles.textVersion}>{ docset.info.softwareVersion }</Nav.Item> :
    undefined

    const toggleSidebar = () => {
      let el = document.getElementById('root')
      if (sidebarVisible) {
        el?.classList.add("showBanner")
        el?.classList.remove("hideBanner")
      } else {
        el?.classList.add('hideBanner')
        el?.classList.remove("showBanner")
      }
      setSidebarVisible(!sidebarVisible)
      setSidebarVisible(!sidebarVisible)
    }
  
    useEffect(() => {
      setSidebarVisible(!isSmallDevice)
    }, [isSmallDevice])

    useEffect(()=>{
      setMetaDescription(docset.info.title.long)
    },[docset])

  return (
    <>
      <DocumentationLayout
        sidebarVisible={sidebarVisible}
        toggleSidebar= {toggleSidebar}
        onSearchClick={() => setShowSearch(true)}
        title={title}
        versionPicker={currentVersion}
        version={VERSION_GROUP_VIYA4 && docset.info.softwareVersion ?  docset.info.softwareVersion : undefined }
        info={docset.info}
        pdf={pdfUrl} 
        onPDFDownloadClick={onPDFDownloadClick}
        side={
          <div className={styles.docsetTocContainer}>
            <TOC alias={alias} page={page} nodes={toc} toggleSidebar={toggleSidebar}/>
          </div>
        }
        content={
          <MainContentArea
            prev={prevLink}
            next={nextLink}
            content={content}
            docsetVersionPicker={versionPicker}
          />
        }
      />
      <SearchModal id={docset.info.id} version={docset.info.version}  currPage={page} type={"docset"} docsets={[docset.info]} show={showSearch} onHide={() => setShowSearch(!showSearch)} filterOptions={[]}/>
    </>
  )
}

export default Docset